import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Globe, BarChart3, Bot, Mail, Phone, Calendar, ExternalLink, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const packageLabels = { website_only: "Website Only", website_dashboard: "Website + AI Dashboard", website_companion: "Website + AI Companion", website_both: "Website + Dashboard + Companion" };
const statusColors = { draft: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", revision: "text-orange-400 bg-orange-400/10 border-orange-400/30", live: "text-green-400 bg-green-400/10 border-green-400/30" };

export default function ClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/clients/${clientId}`, { withCredentials: true })
      .then(res => setClient(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!client) return <div className="text-center py-20 text-[#9999B3]">Client not found</div>;

  const hasDashboard = client.package === "website_dashboard" || client.package === "website_both";
  const hasCompanion = client.package === "website_companion" || client.package === "website_both";

  return (
    <div data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{client.business_name}</h1>
          <p className="text-[#9999B3] text-sm">{client.name}</p>
        </div>
        <span className={`text-sm px-3 py-1.5 rounded-full font-medium capitalize border ${statusColors[client.status] || 'text-[#9999B3]'}`} data-testid="client-status">
          {client.status}
        </span>
      </div>

      {/* Info cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-4">
          <p className="text-[#6B6B85] text-xs mb-1">Package</p>
          <p className="text-white font-semibold text-sm">{packageLabels[client.package]}</p>
        </div>
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-4">
          <p className="text-[#6B6B85] text-xs mb-1">Email</p>
          <a href={`mailto:${client.email}`} className="text-[#C9A84C] text-sm font-medium">{client.email}</a>
        </div>
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-4">
          <p className="text-[#6B6B85] text-xs mb-1">Phone</p>
          <p className="text-white text-sm">{client.phone || "—"}</p>
        </div>
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-4">
          <p className="text-[#6B6B85] text-xs mb-1">Website</p>
          {client.website_url ? (
            <a href={`https://${client.website_url}`} target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] text-sm font-medium flex items-center gap-1">
              {client.website_url} <ExternalLink className="w-3 h-3"/>
            </a>
          ) : <p className="text-[#9999B3] text-sm">Not yet live</p>}
        </div>
      </div>

      {/* Notes */}
      {client.notes && (
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-4 mb-6">
          <p className="text-[#6B6B85] text-xs mb-1 flex items-center gap-1"><FileText className="w-3 h-3"/> Notes</p>
          <p className="text-[#C5C5D8] text-sm">{client.notes}</p>
        </div>
      )}

      {/* Action links */}
      <div className="grid sm:grid-cols-2 gap-3">
        {hasDashboard && (
          <Link to={`/dashboard/client/${clientId}/ai-dashboard`} className="group bg-[#12121F] border border-[#252540] rounded-lg p-5 hover:border-[#C9A84C]/30 transition-all flex items-center gap-4" data-testid="link-ai-dashboard">
            <div className="w-12 h-12 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center"><BarChart3 className="w-6 h-6 text-[#C9A84C]"/></div>
            <div>
              <h3 className="text-white font-semibold text-sm group-hover:text-[#C9A84C] transition-colors">AI Dashboard</h3>
              <p className="text-[#9999B3] text-xs">Leads, traffic, and key metrics</p>
            </div>
          </Link>
        )}
        {hasCompanion && (
          <Link to={`/dashboard/client/${clientId}/ai-companion`} className="group bg-[#12121F] border border-[#252540] rounded-lg p-5 hover:border-[#C9A84C]/30 transition-all flex items-center gap-4" data-testid="link-ai-companion">
            <div className="w-12 h-12 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center"><Bot className="w-6 h-6 text-[#C9A84C]"/></div>
            <div>
              <h3 className="text-white font-semibold text-sm group-hover:text-[#C9A84C] transition-colors">AI Companion</h3>
              <p className="text-[#9999B3] text-xs">Chatbot config and interactions</p>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
